import { useTranslation } from 'next-i18next';
import { ReactElement, useState } from 'react';
import { IError } from '../../../core/shared/checker';
import {
  getErrorById,
  isValidFieldById,
} from '../../../core/shared/checkerUtils';
import { FormControlError } from '../../../core/shared/forms/useForm';
import { NonEmptyArray } from '../../../utils/types';

export type IFieldProps<TID extends string> = {
  type: 'text' | 'number' | 'password' | 'email' | 'date';
  name: string;
  label?: string;
  info?: string;
  placeholder: string;
  value: string;
  min?: string;
  max?: string;
  style?: React.CSSProperties;
  isTouched?: boolean;
  inputMode?:
    | 'numeric'
    | 'none'
    | 'tel'
    | 'decimal'
    | 'email'
    | 'url'
    | 'search';
  onChange: (value: string) => void;
} & (
  | {
      onBlur?: (ID: TID, value: string) => void;
      inputID: TID;
      errors: IError[];
    }
  | {
      onBlur?: (value: string) => void;
      errors?: NonEmptyArray<FormControlError>;
    }
);

export const InputField = <TID extends string>(
  props: IFieldProps<TID>
): ReactElement => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  let isInErrorStatus: boolean;
  let errorMessage: string | undefined;
  if ('inputID' in props) {
    const isValid = isValidFieldById(props.errors, props.inputID);
    const error: IError = getErrorById(props.errors, props.inputID);
    errorMessage = isValid ? '' : t(error.resourceKey);
    isInErrorStatus = !isValid && !!props.isTouched;
  } else {
    errorMessage = props.errors?.map(({ message }) => message).join(', ');
    isInErrorStatus = !!errorMessage && !!props.isTouched;
  }

  return (
    <div className="login__form__field__row" style={props.style}>
      {props.label && (
        <label className="login__form__field__row__label">{props.label}</label>
      )}
      {props.info && (
        <label className="login__form__field__row__info">{props.info}</label>
      )}
      <input
        name={props.name}
        type={showPassword ? 'text' : props.type}
        inputMode={props.inputMode}
        className={`input_style1 ${
          isInErrorStatus ? 'input_style1--error' : ''
        } ${props.type === 'password' ? 'input_style1--password' : ''}`}
        placeholder={props.placeholder}
        value={props.value}
        min={props.min}
        max={props.max}
        data-error={props.errors?.length ? 'error' : ''}
        onBlur={(e) => {
          if (!props.onBlur) return;
          if ('inputID' in props) {
            props.onBlur(props.inputID, props.value);
          } else {
            props.onBlur(props.value);
          }
        }}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
      />
      {props.type === 'password' && (
        <div
          className={`ico_pwd ${
            showPassword ? 'ico_pwd--hide' : 'ico_pwd--show'
          }`}
          onClick={() => setShowPassword(!showPassword)}
        ></div>
      )}
      {isInErrorStatus && (
        <p className="assistive_text assistive_text--error">{errorMessage}</p>
      )}
    </div>
  );
};

InputField.defaultProps = {
  isTouched: true,
};
